import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { RuleObject } from 'antd/es/form';
import type { FormConfig, UseUserForm, UserFormState } from '../UserForm.types';
import { NAMESPACE } from '@/constants';
import { ALLOWED_EMAIL_DOMAINS, isAllowedEmailDomain } from '@/constants/auth';

import { FORM_FIELD } from '../UserForm.constants';
import { useCreateUserMutation, useUpdateUserMutation } from '@/api';

export const useUserForm: UseUserForm = ({ user, close }) => {
  const { t } = useTranslation(NAMESPACE.USER_SETTINGS_PAGE);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [updateUser, { data: updatedUser, isLoading: isUserUpdating }] = useUpdateUserMutation();
  const [createUser, { data: createdUser, isLoading: isUserCreating }] = useCreateUserMutation();
  const [form] = Form.useForm<UserFormState>();

  const handleSubmit = useCallback(() => {
    form.validateFields().then((data) => {
      if (user) {
        updateUser({ ...data, _id: user._id });
      } else {
        createUser(data);
      }
    });
  }, [createUser, form, updateUser, user]);

  useEffect(() => {
    if (updatedUser || createdUser) {
      close();
      message.success({
        content: updatedUser ? t('userHasBeenEdited') : t('userHasBeenCreated'),
        duration: 3
      });
    }
  }, [t, updatedUser, createdUser, close]);

  const handleValuesChange = () => setIsFormTouched(true);

  const validateEmail = useCallback(
    (_: RuleObject, value: string) => {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!pattern.test(value)) {
        return Promise.reject(new Error(t('emailFormatError')));
      }

      /* The same domains sign-in accepts, so a TapTap user can be added as
         well as signed in; this was hard-coded to @skyri.se alone. */
      if (!isAllowedEmailDomain(value)) {
        return Promise.reject(
          new Error(
            t('emailDomainError', {
              domains: ALLOWED_EMAIL_DOMAINS.map((domain) => `@${domain}`).join(' or ')
            })
          )
        );
      }

      return Promise.resolve();
    },
    [t]
  );

  const config: FormConfig = useMemo(() => {
    const requiredFieldMessage = t('requiredFieldMessage', { ns: NAMESPACE.COMMON });

    return {
      [FORM_FIELD.FIRST_NAME]: {
        label: t('firstName'),
        name: 'firstName',
        rules: [{ required: true, whitespace: true, type: 'string', message: requiredFieldMessage }]
      },
      [FORM_FIELD.LAST_NAME]: {
        label: t('lastName'),
        name: 'lastName',
        rules: [{ required: true, type: 'string', message: requiredFieldMessage }]
      },
      [FORM_FIELD.EMAIL]: {
        label: t('email'),
        name: 'email',
        rules: [
          { required: true, message: requiredFieldMessage },
          { validator: validateEmail, type: 'email', validateTrigger: 'submit' }
        ]
      },
      [FORM_FIELD.ROLE]: {
        label: t('role'),
        name: 'role',
        rules: [{ required: true, type: 'string', message: requiredFieldMessage }]
      }
    };
  }, [t, validateEmail]);

  return {
    form,
    config,
    isLoading: isUserCreating || isUserUpdating,
    isFormTouched,
    setIsConfirmModalOpen,
    isConfirmModalOpen,
    handleSubmit,
    handleValuesChange
  };
};
